// Central API client: URL helpers, authenticated fetch, and 401 handling.

export const getApiUrl = () => {
  return import.meta.env.VITE_API_URL || 'http://localhost:8000';
};

export const getWsUrl = () => {
  const baseUrl = import.meta.env.VITE_WS_URL || 'ws://localhost:8000';
  return baseUrl.replace(/^http/, 'ws'); // Ensure ws:// protocol
};

export const getWebSocketUrl = (path: string) => {
  const wsUrl = getWsUrl();
  return `${wsUrl}${path}`;
};

const TOKEN_KEY = 'auth_token';

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

// AuthContext registers a handler so an expired/invalid token logs the user
// out everywhere instead of every page failing silently.
let unauthorizedHandler: (() => void) | null = null;

export function setUnauthorizedHandler(handler: (() => void) | null) {
  unauthorizedHandler = handler;
}

function handleUnauthorized() {
  localStorage.removeItem(TOKEN_KEY);
  unauthorizedHandler?.();
}

export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function authHeaders(contentType = false): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (contentType) {
    headers['Content-Type'] = 'application/json';
  }
  return headers;
}

async function errorMessageFrom(response: Response, fallback: string): Promise<string> {
  try {
    const contentType = response.headers.get('content-type');
    if (contentType?.includes('application/json')) {
      const error = await response.json();
      return error.detail || fallback;
    }
    return `HTTP ${response.status}: ${response.statusText}`;
  } catch {
    return fallback;
  }
}

interface ApiFetchOptions extends RequestInit {
  /** When set, sent as the JSON body with Content-Type: application/json */
  json?: unknown;
}

/**
 * Authenticated fetch returning parsed JSON.
 *
 * - Attaches the Bearer token from localStorage when present
 * - Throws ApiError with the backend's `detail` message on failure
 * - On 401 with a token present, clears the token and notifies AuthContext
 */
export async function apiFetch<T = unknown>(
  path: string,
  options: ApiFetchOptions = {}
): Promise<T> {
  const { json, headers, ...rest } = options;
  const hadToken = getAuthToken() !== null;

  const response = await fetch(`${getApiUrl()}${path}`, {
    ...rest,
    headers: {
      ...authHeaders(json !== undefined),
      ...(headers as Record<string, string> | undefined),
    },
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  });

  if (response.status === 401 && hadToken) {
    handleUnauthorized();
  }
  if (!response.ok) {
    throw new ApiError(
      response.status,
      await errorMessageFrom(response, `Request failed: ${response.status}`)
    );
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return response.json();
}

// Raw-Response wrapper for callers that need the Response object.
// Shares the same auth/401 behavior as apiFetch.
async function rawRequest(path: string, init: RequestInit): Promise<Response> {
  const hadToken = getAuthToken() !== null;
  const response = await fetch(`${getApiUrl()}${path}`, init);

  if (response.status === 401 && hadToken) {
    handleUnauthorized();
  }
  if (!response.ok) {
    throw new ApiError(
      response.status,
      await errorMessageFrom(response, `API error: ${response.statusText}`)
    );
  }
  return response;
}

export const api = {
  get: (path: string) => rawRequest(path, { headers: authHeaders() }),

  post: (path: string, body?: BodyInit) =>
    rawRequest(path, {
      method: 'POST',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body,
    }),

  put: (path: string, body?: BodyInit) =>
    rawRequest(path, {
      method: 'PUT',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body,
    }),

  delete: (path: string) => rawRequest(path, { method: 'DELETE', headers: authHeaders() }),
};
